import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { DownloadSimple, WarningCircle, ShieldCheck, Info, HandHeart, FileText, CaretDown, CaretUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { buildReadableExport } from "@/lib/exportDocument";
import { useAuth } from "@/context/AuthContext";
import TwoFactorSection from "@/components/TwoFactorSection";
import { TermsContent } from "@/components/TermsContent";

const CONFIRM_PHRASE = "delete my account and all my writing";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

export default function SettingsPage() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [phrase, setPhrase] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [planBusy, setPlanBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelledUntil, setCancelledUntil] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  const cancelPlan = async () => {
    setPlanBusy(true);
    try {
      const { data } = await api.post("/subscription/cancel");
      await refresh();
      setConfirmCancel(false);
      setCancelledUntil(data?.access_until || user?.access_until || null);
      toast.success("Done. Nothing more will be charged.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel right now.");
    } finally {
      setPlanBusy(false);
    }
  };

  const openBillingPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/subscription/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open the billing page right now.");
      setPortalBusy(false);
    }
  };

  const resumePlan = async () => {
    setPlanBusy(true);
    try {
      await api.post("/subscription/resume");
      await refresh();
      setCancelledUntil(null);
      toast.success("Welcome back. Your subscription continues.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resume right now.");
    } finally {
      setPlanBusy(false);
    }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/me/export");
      const blob = new Blob([buildReadableExport(data)], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Clarity Mirror, my writing (${new Date().toISOString().slice(0, 10)}).txt`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your writing has been downloaded.");
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const doDelete = async () => {
    if (phrase.trim().toLowerCase() !== CONFIRM_PHRASE) {
      toast.error("The confirmation phrase doesn't match.");
      return;
    }
    setDeleting(true);
    try {
      await api.post("/me/delete", { password: pw, confirm: phrase });
      toast.success("Your account is gone. Take care.");
      logout();
      setTimeout(() => nav("/"), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Deletion failed.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="px-5 sm:px-10 md:px-20 py-10 sm:py-14 max-w-3xl" data-testid="settings-page">
      <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ink-mute">Your account</p>
      <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-ink mt-3 mb-10 leading-tight">
        Settings &amp; your data.
      </h1>

      {/* Account */}
      <section className="rounded-3xl bg-canvas-warm border border-line p-6 sm:p-8 mb-6" data-testid="account-card">
        <h2 className="font-serif-display text-2xl text-ink mb-4">Account</h2>
        <div className="space-y-2 text-sm">
          <Row label="Name" value={user?.name} />
          <Row label="Email" value={user?.email} />
          <Row label="Member since" value={user?.created_at?.slice(0, 10)} />
          <Row label="Access until" value={fmtDate(user?.access_until)} />
          <Row label="Status" value={user?.subscription_status} />
          {user?.plan && (
            <Row label="Renews" value={user?.auto_renew ? "Automatically" : "No, nothing will be charged"} />
          )}
        </div>
      </section>

      {/* Plan */}
      <section className="rounded-3xl bg-canvas-warm border border-line p-6 sm:p-8 mb-6" data-testid="plan-section">
        <div className="flex items-start gap-3 mb-5">
          <HandHeart size={22} weight="thin" className="text-clay shrink-0 mt-1" />
          <div>
            <h2 className="font-serif-display text-2xl text-ink">Your subscription</h2>
            <p className="text-sm text-ink-soft mt-1 leading-relaxed" data-testid="plan-summary">
              {!user?.plan ? (
                user?.subscription_status === "lapsed" ? (
                  <>
                    Your free trial ended on <b>{fmtDate(user?.access_until)}</b>. Everything you’ve written is still here and still yours. Choose a plan below and you can pick up exactly where you left off.
                    {user?.locked_annual_price != null && (
                      <> Your code is on this account, so the yearly plan is <b>${user.locked_annual_price.toFixed(2)}</b> for you.</>
                    )}
                  </>
                ) : (
                  <>
                    You’re on the free trial until <b>{fmtDate(user?.access_until)}</b>, with no card on file, nothing to cancel and nothing that will ever be charged on its own. If the practice is meaningful, you can{" "}
                    <Link to="/app/subscribe" className="underline underline-offset-4 text-clay hover:text-clay-dark">continue whenever you’re ready</Link>.
                  </>
                )
              ) : user?.plan_cancelled ? (
                <>
                  Your <b>{user.plan}</b> subscription is cancelled and won’t renew, so nothing more will be charged. You keep full access until <b>{fmtDate(user?.access_until)}</b>, and every entry and reflection stays yours after that.
                </>
              ) : !user?.has_subscription ? (
                <>
                  You have access until <b>{fmtDate(user?.access_until)}</b> from an earlier one-time payment. Nothing renews on its own and nothing will be charged. When it ends, you can{" "}
                  <Link to="/app/subscribe" className="underline underline-offset-4 text-clay hover:text-clay-dark">start a subscription</Link> whenever you like.
                </>
              ) : user?.sub_status === "trialing" ? (
                <>
                  You’re on the <b>{user.plan}</b> plan, and your free days run until <b>{fmtDate(user?.access_until)}</b>. That’s when your first charge happens, and it renews automatically after that. Cancel any time before then and you won’t be charged at all.
                </>
              ) : (
                <>
                  You’re on the <b>{user.plan}</b> plan. It renews automatically on <b>{fmtDate(user?.access_until)}</b>, when your card is charged again. Cancel any time, and you always keep access to the end of the period you’ve paid for.
                </>
              )}
            </p>
            {user?.plan === "monthly" && user?.has_subscription && !user?.plan_cancelled && (
              <p className="text-sm text-ink-soft mt-2 leading-relaxed" data-testid="upgrade-hint">
                Prefer to think about it once a year?{" "}
                <Link to="/app/subscribe" className="underline underline-offset-4 text-clay hover:text-clay-dark">
                  Switch to the yearly plan
                </Link>{" "}
                . You only pay the difference, and it works out 25% cheaper.
              </p>
            )}
          </div>
        </div>

        {cancelledUntil && user?.plan_cancelled && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-5 rounded-2xl bg-canvas border border-line p-5"
            data-testid="cancel-confirmed"
          >
            <p className="text-sm text-ink leading-relaxed">
              That’s done. Your subscription is paused after <b data-testid="cancel-end-date">{fmtDate(cancelledUntil)}</b>. That’s the last day of writing on this period, and nothing will be charged again. You’re welcome back any time.
            </p>
          </motion.div>
        )}

        {!user?.plan && (
          <Link
            to="/app/subscribe"
            data-testid="choose-plan-btn"
            className="inline-flex rounded-full bg-ink text-canvas px-6 py-2.5 text-sm font-medium hover:bg-ink/90 transition-colors"
          >
            {user?.subscription_status === "lapsed" ? "Choose a plan" : "See the plans"}
          </Link>
        )}

        {user?.plan && !confirmCancel && (
          <div className="flex flex-wrap items-center gap-3">
            {user?.has_subscription && (
              <button
                onClick={openBillingPortal}
                disabled={portalBusy}
                data-testid="billing-portal-btn"
                className="rounded-full border border-line-strong text-ink px-6 py-2.5 text-sm hover:bg-canvas transition-colors disabled:opacity-60"
              >
                {portalBusy ? "Opening…" : "Update card & receipts"}
              </button>
            )}
            {user?.plan_cancelled ? (
              <button
                onClick={resumePlan}
                disabled={planBusy}
                data-testid="resume-plan-btn"
                className="rounded-full bg-ink text-canvas px-6 py-2.5 text-sm font-medium hover:bg-ink/90 transition-colors disabled:opacity-60"
              >
                {planBusy ? "One moment…" : "Resume my subscription"}
              </button>
            ) : user?.has_subscription ? (
              <button
                onClick={() => setConfirmCancel(true)}
                data-testid="cancel-plan-btn"
                className="rounded-full border border-line-strong text-ink-soft px-6 py-2.5 text-sm hover:text-ink hover:bg-canvas transition-colors"
              >
                Cancel my subscription
              </button>
            ) : null}
          </div>
        )}

        {user?.plan && confirmCancel && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl bg-canvas border border-line p-6"
            data-testid="cancel-confirm-panel"
          >
            <p className="font-serif-display text-xl text-ink mb-3">Are you sure?</p>
            <p className="text-sm text-ink-soft leading-relaxed mb-5">
              You’ll keep full access until <b>{fmtDate(user?.access_until)}</b>. After that your account is paused. Your writing stays safely here and yours to download, and nothing more will be charged.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={cancelPlan}
                disabled={planBusy}
                data-testid="confirm-cancel-btn"
                className="rounded-full bg-ink text-canvas px-6 py-2.5 text-sm font-medium hover:bg-ink/90 transition-colors disabled:opacity-60"
              >
                {planBusy ? "One moment…" : "Yes, cancel"}
              </button>
              <button
                onClick={() => setConfirmCancel(false)}
                disabled={planBusy}
                data-testid="keep-plan-btn"
                className="rounded-full border border-line-strong text-ink px-6 py-2.5 text-sm hover:bg-canvas-warm transition-colors disabled:opacity-60"
              >
                Actually, keep it
              </button>
            </div>
          </motion.div>
        )}
      </section>

      {/* 2FA */}
      <TwoFactorSection />

      {/* Export */}
      <section className="rounded-3xl bg-canvas-warm border border-line p-6 sm:p-8 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <DownloadSimple size={22} weight="thin" className="text-clay shrink-0 mt-1" />
          <div>
            <h2 className="font-serif-display text-2xl text-ink">Download your writing</h2>
            <p className="text-sm text-ink-soft mt-1 leading-relaxed">
              Get a readable document you can open on any device: every entry, mood, body signal, and the reflection written back to you. Yours to keep, forever.
            </p>
          </div>
        </div>
        <button
          onClick={exportData}
          disabled={exporting}
          data-testid="export-data-btn"
          className="inline-flex items-center gap-2 rounded-full bg-ink text-canvas px-6 py-2.5 text-sm font-medium hover:bg-ink/90 transition-colors disabled:opacity-60"
        >
          {exporting ? "Preparing…" : "Download my data"}
        </button>
      </section>

      {/* Privacy */}
      <section className="rounded-3xl bg-canvas-warm border border-line p-6 sm:p-8 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <ShieldCheck size={22} weight="thin" className="text-sage shrink-0 mt-1" />
          <div>
            <h2 className="font-serif-display text-2xl text-ink">Privacy at a glance</h2>
          </div>
        </div>
        <ul className="space-y-2 text-sm text-ink-soft leading-relaxed">
          <li>· All traffic is HTTPS/TLS encrypted.</li>
          <li>· Payments are handled by Stripe. No card data ever touches our database.</li>
          <li>· Your entries are sent to Anthropic (via Emergent) to generate reflections. Anthropic does not train on API data.</li>
          <li>· Your entries are encrypted with a key unique to your account.</li>
          <li>· Passwords are hashed with bcrypt + per-user salt. We can never read yours.</li>
          <li>· Nothing else. No analytics, no trackers, no ad networks.</li>
        </ul>
        <Link
          to="/privacy-promise"
          data-testid="settings-privacy-promise-link"
          className="inline-flex items-center gap-2 mt-5 rounded-full border border-line-strong text-ink px-6 py-2.5 text-sm hover:bg-canvas transition-colors"
        >
          Read “How we protect you”
        </Link>
      </section>

      {/* User agreement */}
      <section className="rounded-3xl bg-canvas-warm border border-line p-6 sm:p-8 mb-6" data-testid="agreement-section">
        <div className="flex items-start gap-3 mb-4">
          <FileText size={22} weight="thin" className="text-clay shrink-0 mt-1" />
          <div>
            <h2 className="font-serif-display text-2xl text-ink">The agreement you’re under</h2>
            <p className="text-sm text-ink-soft mt-1 leading-relaxed">
              {user?.terms_accepted_at ? (
                <>You agreed to the user agreement &amp; privacy terms on <b data-testid="agreement-date">{user.terms_accepted_at.slice(0, 10)}</b>. The full text is always here: nothing hidden, nothing behind a link you’ll never click.</>
              ) : (
                <>The full user agreement &amp; privacy terms, always readable here in full.</>
              )}
              {" "}The formal{" "}
              <Link to="/terms-of-service" data-testid="settings-tos-link" className="text-clay hover:text-clay-dark underline underline-offset-4">Terms of Service</Link>{" "}
              (intellectual property, licensing, prohibited uses) is also always available.
            </p>
          </div>
        </div>
        <button
          onClick={() => setTermsOpen((o) => !o)}
          data-testid="toggle-agreement-btn"
          className="inline-flex items-center gap-2 rounded-full border border-line-strong text-ink px-6 py-2.5 text-sm hover:bg-canvas transition-colors"
        >
          {termsOpen ? (
            <>Hide the agreement <CaretUp size={14} weight="bold" /></>
          ) : (
            <>Read the full agreement <CaretDown size={14} weight="bold" /></>
          )}
        </button>
        {termsOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 pt-8 border-t border-line"
            data-testid="agreement-full-text"
          >
            <TermsContent />
          </motion.div>
        )}
      </section>

      {/* Delete */}
      <section className="rounded-3xl bg-clay/5 border border-clay/30 p-6 sm:p-8" data-testid="delete-account-section">
        <div className="flex items-start gap-3 mb-4">
          <WarningCircle size={22} weight="thin" className="text-clay-dark shrink-0 mt-1" />
          <div>
            <h2 className="font-serif-display text-2xl text-ink">Delete your account</h2>
            <p className="text-sm text-ink-soft mt-1 leading-relaxed">
              Permanently remove your account, every entry, every reflection, and every payment record. This cannot be undone. If you want a copy, download your writing first.
            </p>
          </div>
        </div>
        {!confirmOpen ? (
          <button
            onClick={() => setConfirmOpen(true)}
            data-testid="open-delete-account"
            className="rounded-full border border-clay/40 text-clay-dark px-6 py-2.5 text-sm hover:bg-clay/10 transition-colors"
          >
            I want to delete my account
          </button>
        ) : (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-ink-mute">Confirm your password</span>
              <input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                data-testid="delete-password"
                className="mt-2 w-full bg-canvas border border-line rounded-lg px-3 py-2 text-sm focus:border-clay focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-ink-mute">
                Type: <span className="font-mono text-clay-dark">{CONFIRM_PHRASE}</span>
              </span>
              <input
                type="text"
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                data-testid="delete-phrase"
                className="mt-2 w-full bg-canvas border border-line rounded-lg px-3 py-2 text-sm focus:border-clay focus:outline-none"
              />
            </label>
            <div className="flex items-center gap-3">
              <button
                onClick={doDelete}
                disabled={deleting || !pw || phrase.trim().toLowerCase() !== CONFIRM_PHRASE}
                data-testid="confirm-delete-account"
                className="rounded-full bg-clay text-canvas px-6 py-2.5 text-sm font-medium hover:bg-clay-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {deleting ? "Deleting…" : "Delete forever"}
              </button>
              <button
                onClick={() => { setConfirmOpen(false); setPw(""); setPhrase(""); }}
                className="text-sm text-ink-soft hover:text-ink underline underline-offset-4 decoration-line-strong"
              >
                Never mind
              </button>
            </div>
          </motion.div>
        )}
      </section>

      <p className="mt-10 text-xs text-ink-mute italic flex items-center gap-1.5">
        <Info size={12} weight="thin" />
        Read the full <a href="/terms" className="underline underline-offset-4 hover:text-ink">terms and privacy</a>.
      </p>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-line last:border-0 py-2">
      <span className="text-ink-mute uppercase tracking-[0.15em] text-[11px]">{label}</span>
      <span className="text-ink font-mono text-xs">{value || "—"}</span>
    </div>
  );
}
